import styled from "@emotion/styled";
import { sortBy } from "lodash";
import { useCallback, useState } from "react";
import * as React from "react";
import { RouteComponentProps } from "react-router";

import { AddCircle, Close } from "@livechat/design-system-icons";
import {
  DesignToken,
  Heading,
  Icon,
  SpacingToken,
  Tab,
  TabsList,
  TabsWrapper,
  Text,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { ChangesNotSavedModal } from "components/modals/ChangesNotSavedModal";
import { newFeaturesCampaigns } from "constants/campaigns";
import { EMPTY_RULE, PREMADE_RULES_COLLECTION } from "constants/rules/premade-rules";
import { isInIframe } from "helpers/app";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { AutomationsRoutes, WORKFLOW_CHANGES_NOT_SAVED_MODAL_DESC } from "routes/automations/constants";
import * as RuleSerializer from "services/api/rules/rule-serializer";
import { IRule } from "store/entities/rules/interfaces";
import { withVar } from "themes/helpers";

import WorkflowsViewContainer from "../components/FullPageViewContainer";
import RuleEditor from "../components/editor";
import { ActionLink, StyledContentWrapper, WorkflowsGrid } from "../components/styled";
import { PageWrapper } from "../styled";
import { trackWorkflowCreationStarted } from "../utils";

enum WorkflowStep {
  CreateSelect = "create-select",
  Editing = "rule-editor",
}

type AddWorkflowState =
  | { type: WorkflowStep.CreateSelect }
  | {
      type: WorkflowStep.Editing;
      rule: IRule;
      fromTemplate: string | null;
      duplicated?: string;
    };

interface ISelectAddVariantProps {
  setPageState(state: AddWorkflowState): void;
  category: PremadeCategory;
  setCategory(newCategory: PremadeCategory): void;
}

enum PremadeCategory {
  All = "ALL",
  AutoMessaging = "AUTOMSG",
  Vip = "PRIOVIP",
  Assignment = "ASSIGN",
  Following = "FOLLOW",
  Labeling = "LABEL",
  StatusChange = "STATUS",
}

const PREMADE_CATEGORIES = {
  [PremadeCategory.All]: {
    label: "All",
    description: "Browse all available automation rule templates and find the ones that will speed up your daily work.",
  },
  [PremadeCategory.AutoMessaging]: {
    label: "Auto-messaging",
    description: "Communicate proactively and exchange messages with the requester without direct input.",
  },
  [PremadeCategory.Vip]: {
    label: "Prioritizing and VIP",
    description:
      "Set the urgency level and priority for resolving regular or VIP tickets. Meet your SLA requirements flawlessly.",
  },
  [PremadeCategory.Assignment]: {
    label: "Assignment",
    description: "Speed up the resolution process and route tickets to the qualified people and teams.",
  },
  [PremadeCategory.Following]: {
    label: "Following",
    description: "Track the ticket resolution process and stay informed of any updates.",
  },
  [PremadeCategory.Labeling]: {
    label: "Labeling",
    description: "Refine your tickets and equip them with helpful and descriptive tags.",
  },
  [PremadeCategory.StatusChange]: {
    label: "Status change",
    description: "Be transparent and set the ticket status to match the actual situation.",
  },
};

const [
  FEEDBACK_REQUEST_RULE,
  PENDING_TO_CLOSED_RULE,
  FOLLOW_UP_RULE,
  NEW_TICKET_CONFIRMATION_RULE,
  SECOND_FOLLOW_UP_RULE,
  PRIVATE_NOTE_ON_TEAM_CHANGE,
  SET_HIGH_PRIORITY_ON_REQUESTER,
  SET_HIGH_PRIORITY_AFTER_INACTIVITY,
  SET_PRIORITY_TO_URGENT,
  TICKET_ESCALATION,
  SET_ASSIGNMENT_BASED_ON_SUBJECT,
  SET_ASSIGNMENT_BASED_ON_MESSAGE,
  SET_ASSIGNMENT_GENERAL_RANDOMLY,
  SET_ASSIGNMENT_REQUESTER,
  ADD_FOLLOWER_ON_TAG,
  ADD_FOLLOWER_BASED_ON_ASSIGNMENT_CHANGE,
  ADD_TAG_BASED_ON_TEXT,
  ADD_TAG_ON_LC_INTEGRATION,
  ADD_TAG_ON_REQUESTER,
  ADD_TAG_ON_TEAM,
  ADD_TAG_ON_FORWARDING_ADDRESS,
  SET_TO_SOLVED_ON_TEXT,
  LANGUAGE_BASED_ASSIGNMENT,
] = PREMADE_RULES_COLLECTION;

const CATEGORY_ASSIGNMENTS = {
  [PremadeCategory.All]: null,
  [PremadeCategory.AutoMessaging]: [
    NEW_TICKET_CONFIRMATION_RULE,
    FOLLOW_UP_RULE,
    SECOND_FOLLOW_UP_RULE,
    FEEDBACK_REQUEST_RULE,
    PRIVATE_NOTE_ON_TEAM_CHANGE,
  ],
  [PremadeCategory.Vip]: [
    SET_HIGH_PRIORITY_ON_REQUESTER,
    SET_HIGH_PRIORITY_AFTER_INACTIVITY,
    SET_PRIORITY_TO_URGENT,
    TICKET_ESCALATION,
  ],
  [PremadeCategory.Assignment]: [
    SET_ASSIGNMENT_BASED_ON_SUBJECT,
    SET_ASSIGNMENT_BASED_ON_MESSAGE,
    SET_ASSIGNMENT_GENERAL_RANDOMLY,
    SET_ASSIGNMENT_REQUESTER,
    LANGUAGE_BASED_ASSIGNMENT,
  ],
  [PremadeCategory.Following]: [ADD_FOLLOWER_ON_TAG, ADD_FOLLOWER_BASED_ON_ASSIGNMENT_CHANGE],
  [PremadeCategory.Labeling]: [
    ADD_TAG_BASED_ON_TEXT,
    ADD_TAG_ON_LC_INTEGRATION,
    ADD_TAG_ON_REQUESTER,
    ADD_TAG_ON_TEAM,
    ADD_TAG_ON_FORWARDING_ADDRESS,
  ],
  [PremadeCategory.StatusChange]: [SET_TO_SOLVED_ON_TEXT, PENDING_TO_CLOSED_RULE],
};

const CATEGORY_ORDER = [
  PremadeCategory.All,
  PremadeCategory.AutoMessaging,
  PremadeCategory.Vip,
  PremadeCategory.Assignment,
  PremadeCategory.Following,
  PremadeCategory.Labeling,
  PremadeCategory.StatusChange,
];

const SelectAddVariant: React.FC<ISelectAddVariantProps> = ({ setPageState, category, setCategory }) => {
  const { isNewForAgent, featureFlagValue, setAgentFlagCallback } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasSeenLanguageCondition.flag,
    newFeaturesCampaigns.hasSeenLanguageCondition.campaignStartDate
  );

  const rules = sortBy(CATEGORY_ASSIGNMENTS[category] || PREMADE_RULES_COLLECTION, r => {
    const categoryIndex = CATEGORY_ORDER.findIndex(c => (CATEGORY_ASSIGNMENTS[c] || []).includes(r));

    return [categoryIndex, r.name];
  });

  const proceedToEditor = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (rule?: any) => (event: any) => {
      event.preventDefault();
      const startRule = rule ? RuleSerializer.deserializeRule(rule) : RuleSerializer.deserializeRule(EMPTY_RULE);
      trackWorkflowCreationStarted("add", rule ? rule.premadeType : null);

      if (rule?.premadeType === "LANGUAGE_BASED_ASSIGNMENT" && isNewForAgent && !featureFlagValue) {
        setAgentFlagCallback();
      }

      setPageState({
        type: WorkflowStep.Editing,
        rule: startRule,
        fromTemplate: rule ? rule.premadeType : null,
      });
    },
    [setPageState]
  );

  return (
    <div>
      <SectionHeading size="xs">Create custom rule</SectionHeading>
      <Block marginBottom={withVar(SpacingToken.Spacing8)} maxWidth="400px">
        <ActionLink role="button" onClick={proceedToEditor()} data-testid="create-custom-workflow-grid-link">
          <Icon source={AddCircle} size="large" />
          <CardHeading size="xs">New custom rule</CardHeading>
          <CardDescription>Create your own rule based on a set of prepared conditions and actions.</CardDescription>
        </ActionLink>
      </Block>
      <SectionHeading size="xs">Use templates</SectionHeading>
      <TabsWrapper>
        <TabsList>
          {CATEGORY_ORDER.map(c => (
            <Tab
              key={c}
              isSelected={c === category}
              onClick={() => setCategory(c)}
              count={CATEGORY_ASSIGNMENTS[c]?.length}
            >
              {PREMADE_CATEGORIES[c].label}
            </Tab>
          ))}
        </TabsList>
      </TabsWrapper>
      <Text
        customColor={withVar(DesignToken.ContentBasicSecondary)}
        css={{ marginTop: withVar(SpacingToken.Spacing3) }}
      >
        {PREMADE_CATEGORIES[category].description}
      </Text>
      <WorkflowsGrid>
        {rules.map(rule => (
          <ActionLink key={rule.name} role="button" onClick={proceedToEditor(rule)}>
            <Icon source={rule.icon} size="xxlarge" />
            <CardHeading size="xs">{rule.name}</CardHeading>
            <CardDescription>{rule.description}</CardDescription>
          </ActionLink>
        ))}
      </WorkflowsGrid>
    </div>
  );
};

const getTitle = (pageState: AddWorkflowState): string => {
  switch (pageState.type) {
    case WorkflowStep.Editing:
      return pageState.fromTemplate ? "New rule from template" : "New custom rule";
    default:
      return "New rule";
  }
};

interface ILocationState {
  rule?: string;
  duplicatedRule?: IRule;
  duplicatedRuleId?: string;
  fromTemplate?: IRule["premadeType"];
}

const getPageState = (state: ILocationState): AddWorkflowState => {
  const parsedRule = state?.rule ? JSON.parse(state.rule) : null;
  if (state?.duplicatedRule) {
    return {
      type: WorkflowStep.Editing,
      rule: state?.duplicatedRule,
      fromTemplate: null,
      duplicated: state?.duplicatedRuleId,
    };
  }
  if (state?.rule) {
    return {
      type: WorkflowStep.Editing,
      rule: RuleSerializer.deserializeRule(parsedRule),
      fromTemplate: parsedRule.premadeType,
    };
  }
  return { type: WorkflowStep.CreateSelect };
};

const AddWorkflow: React.FC<RouteComponentProps> = ({ location, history }) => {
  const [pageState, setPageState] = useState<AddWorkflowState>(getPageState((location.state as ILocationState) || {}));
  const [category, setCategory] = useState(PremadeCategory.All);
  const [formPristine, setFormPristine] = useState(true);
  const [changesNotSavedModalOpen, setChangesNotSavedModalOpen] = useState(false);
  const embeddedInIframe = isInIframe();

  const handleBackToList = (pristine: boolean): void => {
    if (pristine) {
      history.push(AutomationsRoutes.Rules);
    } else {
      setChangesNotSavedModalOpen(true);
    }
  };

  const handleDiscardChanges = (): void => {
    history.push(AutomationsRoutes.Rules);
  };

  const handleBackToEditing = (): void => {
    setChangesNotSavedModalOpen(false);
  };

  const handleBackButton = (pristine: boolean): void => {
    if (pristine) {
      if (
        pageState.type === WorkflowStep.CreateSelect ||
        (pageState.type === WorkflowStep.Editing &&
          ((location.state as ILocationState)?.duplicatedRule || (location.state as ILocationState)?.rule))
      ) {
        history.push(AutomationsRoutes.Rules);
      } else {
        setPageState({ type: WorkflowStep.CreateSelect });
      }
    } else {
      setChangesNotSavedModalOpen(true);
    }
  };

  return (
    <WorkflowsViewContainer
      title={getTitle(pageState)}
      onBack={() => handleBackButton(formPristine)}
      backButtonIcon={embeddedInIframe && pageState.type === WorkflowStep.CreateSelect ? Close : undefined}
    >
      {pageState.type === WorkflowStep.CreateSelect && (
        <PageWrapper>
          <StyledContentWrapper>
            <SelectAddVariant setPageState={setPageState} category={category} setCategory={setCategory} />
          </StyledContentWrapper>
        </PageWrapper>
      )}
      {pageState.type === WorkflowStep.Editing && (
        <PageWrapper>
          <StyledContentWrapper>
            <RuleEditor
              rule={pageState.rule}
              fromTemplate={pageState.fromTemplate}
              duplicated={pageState.duplicated}
              setFormPristine={setFormPristine}
              handleBackToList={handleBackToList}
            />
            <ChangesNotSavedModal
              isOpen={changesNotSavedModalOpen}
              onDiscardChanges={handleDiscardChanges}
              onBackToEditing={handleBackToEditing}
            >
              {WORKFLOW_CHANGES_NOT_SAVED_MODAL_DESC}
            </ChangesNotSavedModal>
          </StyledContentWrapper>
        </PageWrapper>
      )}
    </WorkflowsViewContainer>
  );
};

const CardHeading = styled(Heading)`
  margin-top: var(${SpacingToken.Spacing3});
`;

const CardDescription = styled(Text)`
  margin-top: var(${SpacingToken.Spacing2});
  color: var(${DesignToken.ContentBasicSecondary});
`;

const SectionHeading = styled(Heading)`
  margin-bottom: var(${SpacingToken.Spacing2});
`;

export default AddWorkflow;
